import React from "react";
import { useNavigate } from "react-router-dom";
import heroImage from "../../assets/M2.png";
import { 
  Target, Eye, Heart, Shield, 
  Award, Users, TrendingUp, Zap,
  CheckCircle, Globe, Clock, BarChart3,
  Star, Building, Sparkles, ArrowRight,
  Award as Trophy, DollarSign, Lock
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();
  
  const stats = [
    { value: "5,000+", label: "Happy Customers", icon: Users, color: "from-blue-500 to-cyan-500" },
    { value: "₹250M+", label: "Loans Disbursed", icon: DollarSign, color: "from-purple-500 to-pink-500" },
    { value: "98%", label: "Approval Rate", icon: TrendingUp, color: "from-emerald-500 to-green-500" },
    { value: "24/7", label: "Support", icon: Clock, color: "from-amber-500 to-orange-500" },
  ];

  const values = [
    {
      icon: Shield,
      title: "Security First",
      description: "Bank-level encryption and compliance with global security standards",
      points: ["256-bit SSL Encryption", "GDPR Compliance", "Regular Security Audits"]
    },
    {
      icon: Target,
      title: "Customer-Centric",
      description: "Designed with our customers' needs at the forefront",
      points: ["24/7 Support", "Personalized Solutions", "Transparent Pricing"]
    },
    {
      icon: Zap,
      title: "Innovation Driven",
      description: "Leveraging cutting-edge technology for better experiences",
      points: ["AI-Powered Approvals", "Real-time Analytics", "Automated Processes"]
    },
    {
      icon: Heart,
      title: "Integrity",
      description: "Building trust through honesty and ethical practices",
      points: ["No Hidden Fees", "Clear Terms", "Fair Treatment"]
    }
  ];

  const milestones = [
    { year: "2020", title: "Founded", description: "Smart Lend was established with a vision to revolutionize lending" },
    { year: "2021", title: "First 1000 Customers", description: "Reached our first major customer milestone" },
    { year: "2022", title: "Series A Funding", description: "Secured $5M in funding to expand operations" },
    { year: "2023", title: "National Expansion", description: "Expanded services across all major cities" },
    { year: "2024", title: "Award Recognition", description: "Won 'Best FinTech Innovation' award" },
  ];

  const team = [
    { name: "Sarah Chen", role: "CEO & Founder", expertise: "FinTech Innovation", experience: "15+ years" },
    { name: "Michael Rodriguez", role: "CTO", expertise: "Technology & Security", experience: "12+ years" },
    { name: "Priya Sharma", role: "Head of Operations", expertise: "Customer Experience", experience: "10+ years" },
    { name: "David Kim", role: "Head of Risk", expertise: "Financial Risk Management", experience: "14+ years" },
  ];

  const features = [
    { icon: BarChart3, title: "Smart Analytics", description: "AI-driven insights for better financial decisions" },
    { icon: Lock, title: "Bank-Grade Security", description: "Enterprise-level protection for your data" },
    { icon: Globe, title: "Digital-First", description: "Access your account anywhere, anytime" },
    { icon: CheckCircle, title: "Quick Approvals", description: "Get decisions in minutes, not days" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                <Building className="w-4 h-4" />
                <span className="text-sm font-medium">About Smart Lend</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Revolutionizing <span className="text-cyan-300">Lending</span> with Intelligence
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 max-w-2xl leading-relaxed">
                Smart Lend is a cutting-edge financial technology platform that combines artificial intelligence, 
                advanced security, and user-centric design to transform how people access and manage loans.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-cyan-300" />
                  <span className="font-medium">Award-Winning Platform</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-300" />
                  <span className="font-medium">Bank-Level Security</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-cyan-300" />
                  <span className="font-medium">4.9/5 Customer Rating</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={heroImage} 
                  alt="Smart Lend Platform" 
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent"></div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-2xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">₹250M+</div>
                  <div className="text-sm text-gray-600">Loans Disbursed</div>
                  <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                    <TrendingUp className="w-4 h-4" />
                    <span>+45% YoY</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${stat.color} mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border border-blue-100 p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
                <p className="text-gray-600">What drives us forward</p>
              </div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              To democratize access to financial services by creating an intelligent, transparent, and secure lending 
              ecosystem that empowers individuals and businesses to achieve their financial goals.
            </p>
            <div className="space-y-3">
              {["Instant loan approvals", "Transparent pricing", "Personalized solutions", "Exceptional support"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl border border-purple-100 p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Our Vision</h2>
                <p className="text-gray-600">Where we're headed</p>
              </div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              To become the world's most trusted intelligent lending platform, transforming how people access capital 
              and revolutionizing financial inclusion through technology and innovation.
            </p>
            <div className="space-y-3">
              {["Global expansion", "AI-powered insights", "Blockchain integration", "Complete digital transformation"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Core Values */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-full mb-4">
            <Heart className="w-4 h-4" />
            <span className="font-medium">Our Core Values</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">The Principles That Guide Us</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            These values form the foundation of everything we do at Smart Lend
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {value.points.map((point, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full"
                      >
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Our Journey */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 rounded-full mb-4">
              <Trophy className="w-4 h-4" />
              <span className="font-medium">Our Journey</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Milestones & Achievements</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From humble beginnings to industry leadership
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-500 hidden lg:block"></div>
            
            <div className="space-y-8 lg:space-y-0">
              {milestones.map((milestone, index) => {
                const isEven = index % 2 === 0;
                return (
                  <div 
                    key={index}
                    className={`relative lg:flex lg:items-center lg:justify-between ${
                      isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    <div className={`lg:w-5/12 ${isEven ? 'lg:text-right lg:pr-12' : 'lg:pl-12'}`}>
                      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
                        <div className="text-4xl font-bold text-gray-900 mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </div>
                    </div>
                    
                    {/* Timeline dot */}
                    <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2">
                      <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full border-4 border-white shadow-lg"></div>
                    </div>
                    
                    <div className={`lg:w-5/12 ${isEven ? 'lg:pl-12' : 'lg:pr-12'}`}>
                      <div className="h-full flex items-center">
                        <div className={`text-6xl font-bold text-gray-200 opacity-30 ${isEven ? 'text-left' : 'text-right'}`}>
                          0{index + 1}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Leadership Team */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 rounded-full mb-4">
            <Users className="w-4 h-4" />
            <span className="font-medium">Leadership Team</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Leadership</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Experienced professionals driving innovation in financial technology
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                {member.name.split(' ').map(n => n[0]).join('')}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{member.name}</h3>
              <p className="text-blue-600 font-medium mb-3">{member.role}</p>
              
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-500">Expertise:</span>
                  <span className="text-gray-700 font-medium ml-2">{member.expertise}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Experience:</span>
                  <span className="text-gray-700 font-medium ml-2">{member.experience}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button className="text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors flex items-center gap-1">
                  View Profile
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center text-white mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Why Choose Smart Lend?</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              We combine cutting-edge technology with exceptional service to deliver the best lending experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-6 text-white hover:bg-white/20 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-blue-100">{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 pt-12 border-t border-white/20 text-center">
            <p className="text-white text-lg mb-6">
              Join thousands of satisfied customers who trust Smart Lend for their financial needs
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;